// Regras puras da tela de Empresas (/companies) e da filha Planos (/plans).
// Separadas dos componentes para ficarem testáveis sem montar o React.
import { i18n } from "../../translate/i18n";

// Quantas mensalidades cabem em um ano, por recorrência do plano
// (`Plans.recurrence`; `Companies.recurrence` é legado e só vale como fallback
// quando o plano não traz recorrência). O valor do plano é sempre o da
// recorrência inteira.
const PARCELAS_POR_ANO = {
  MENSAL: 12,
  BIMESTRAL: 6,
  TRIMESTRAL: 4,
  SEMESTRAL: 2,
  ANUAL: 1,
};

/**
 * `Plan.amount` é string no backend e chega como "149.90" ou "149,90"
 * dependendo de quem gravou. Aceita as duas formas.
 */
export const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;
  const bruto = String(value).replace(/[^\d,.-]/g, "");
  const normalizado = bruto.includes(",")
    ? bruto.replace(/\./g, "").replace(",", ".")
    : bruto;
  const n = parseFloat(normalizado);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Valor MENSAL de uma empresa.
 *
 * O KPI do mockup é "receita mensal recorrente": um plano anual de R$ 899 não
 * são R$ 899 por mês. Recorrência desconhecida é tratada como mensal — é como
 * o backend calcula o próximo vencimento quando não reconhece o valor.
 */
export const valorMensal = (company) => {
  // Com assinatura vigente, o período é o escolhido no checkout: `amount` é
  // o preço mensal e `amountYearly` o anual (12 meses de uma vez).
  const sub = company?.subscription;
  if (sub && ["ACTIVE", "PAST_DUE"].includes(sub.status)) {
    if (sub.period === "ANUAL") {
      const anual = parseAmount(company?.plan?.amountYearly);
      if (anual > 0) return anual / 12;
    }
    return parseAmount(company?.plan?.amount);
  }
  const total = parseAmount(company?.plan?.amount);
  if (!total) {
    // Plano só anual (sem preço mensal): o anual dividido por 12.
    const anual = parseAmount(company?.plan?.amountYearly);
    if (anual > 0) return anual / 12;
  }
  const rec = company?.plan?.recurrence ?? company?.recurrence;
  const parcelas = PARCELAS_POR_ANO[String(rec || "").toUpperCase()];
  if (!parcelas || parcelas === 12) return total;
  return (total * parcelas) / 12;
};

/**
 * A data é exibível? Existem faturas gravadas com a string literal
 * "Invalid date" no `dueDate` — mostrar isso na tela é pior que omitir.
 */
export const dataValida = (valor) =>
  /^\d{4}-\d{2}-\d{2}/.test(String(valor || ""));

// Dias até o vencimento (negativo = vencida); null quando não há data válida.
export const diasAteVencimento = (company, hoje = new Date()) => {
  const bruto = company?.dueDate;
  if (!bruto) return null;
  const dia = String(bruto).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return null;
  const [ano, mes, d] = dia.split("-").map(Number);
  // Comparação em datas locais: `new Date("2026-09-09")` é meia-noite UTC e
  // num fuso negativo cai no dia anterior.
  const venc = new Date(ano, mes - 1, d);
  const base = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((venc - base) / 86400000);
};

/**
 * A empresa está em trial? Não há marcação de trial na empresa: o plano é que
 * diz `trial` / `trialDays`, e vale enquanto a empresa foi criada há menos
 * dias que isso.
 */
export const emTrial = (company, hoje = new Date()) => {
  const plano = company?.plan;
  if (!plano?.trial) return false;
  const dias = Number(plano.trialDays);
  if (!Number.isFinite(dias) || dias <= 0) return false;
  if (!company?.createdAt) return false;
  const criada = new Date(company.createdAt);
  if (Number.isNaN(criada.getTime())) return false;
  const decorridos = Math.floor((hoje - criada) / 86400000);
  return decorridos <= dias;
};

/**
 * Estado da linha: "inactive" | "expired" | "trial" | "soon" | "active".
 *
 * A ordem importa: uma empresa desativada não é "vencida" mesmo com a data no
 * passado — no mockup ela aparece esmaecida, não em vermelho.
 */
export const companyState = (company, hoje = new Date()) => {
  if (company?.status === false) return "inactive";
  const dias = diasAteVencimento(company, hoje);
  if (dias !== null && dias < 0) return "expired";
  if (emTrial(company, hoje)) return "trial";
  if (dias !== null && dias <= 7) return "soon";
  return "active";
};

export const statusLabel = (state) =>
  i18n.t(`aeros.companies.status.${state}`);

/**
 * Texto abaixo da data de vencimento ("em 33 dias", "6 dias em atraso",
 * "conta desativada").
 */
export const dueHint = (company, hoje = new Date()) => {
  if (company?.status === false) return i18n.t("aeros.companies.due.disabled");
  const dias = diasAteVencimento(company, hoje);
  if (dias === null) return "";
  if (dias < 0) {
    const atraso = Math.abs(dias);
    return i18n.t(
      atraso === 1 ? "aeros.companies.due.lateOne" : "aeros.companies.due.late",
      { count: atraso }
    );
  }
  if (dias === 0) return i18n.t("aeros.companies.due.today");
  return i18n.t(
    dias === 1 ? "aeros.companies.due.inOne" : "aeros.companies.due.in",
    { count: dias }
  );
};

/**
 * Os cinco KPIs do topo. Recebe a lista inteira (o endpoint `/companiesPlan`
 * não é paginado) e os saldos de SMS já carregados.
 */
export const companyKpis = (companies = [], hoje = new Date()) => {
  let ativas = 0;
  let inativas = 0;
  let mrr = 0;
  let vencidas = 0;
  let valorEmAtraso = 0;
  let trial = 0;
  let trialExpirando = 0;
  let bytes = 0;
  let arquivos = 0;

  companies.forEach((company) => {
    const state = companyState(company, hoje);
    if (company.status === false) inativas += 1;
    else ativas += 1;

    // MRR conta só quem está ativo: empresa desativada não fatura.
    if (company.status !== false) mrr += valorMensal(company);

    if (state === "expired") {
      vencidas += 1;
      valorEmAtraso += parseAmount(company?.plan?.amount);
    }
    if (state === "trial") {
      trial += 1;
      const dias = diasAteVencimento(company, hoje);
      if (dias !== null && dias <= 7) trialExpirando += 1;
    }

    bytes += Number(company?.metrics?.folderSize) || 0;
    arquivos += Number(company?.metrics?.numberOfFiles) || 0;
  });

  return {
    total: companies.length,
    ativas,
    inativas,
    mrr,
    vencidas,
    valorEmAtraso,
    trial,
    trialExpirando,
    bytes,
    arquivos,
  };
};

/**
 * Filtro da lista por texto (nome, e-mail, documento ou id) e por status.
 */
export const filtraEmpresas = (
  companies = [],
  { termo = "", status = "" } = {},
  hoje = new Date()
) => {
  const t = termo.trim().toLowerCase();
  return companies.filter((company) => {
    if (status && status !== "all") {
      if (status !== companyState(company, hoje)) return false;
    }
    if (!t) return true;
    return [company.name, company.email, company.document, `#${company.id}`]
      .filter(Boolean)
      .some((campo) => String(campo).toLowerCase().includes(t));
  });
};

/**
 * Tamanho legível. O backend manda bytes em `metrics.folderSize`.
 * Base 1024 com os rótulos usuais — é o que a tela antiga já mostrava.
 */
export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n <= 0) return "0 B";
  const unidades = ["B", "KB", "MB", "GB", "TB"];
  let valor = n;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i += 1;
  }
  const casas = valor >= 100 || i === 0 ? 0 : 1;
  return `${valor.toFixed(casas).replace(".", ",")} ${unidades[i]}`;
};

export const formatBRL = (valor, casas = 2) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });

export const initialsOf = (nome = "") =>
  nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

/**
 * Saldo depois da operação no modal de créditos SMS.
 * Nunca deixa o saldo negativo: remover mais do que existe zera.
 */
export const novoSaldoSms = (saldoAtual, quantidade, operacao = "add") => {
  const atual = Number(saldoAtual) || 0;
  const qtd = Number(quantidade) || 0;
  if (qtd <= 0) return atual;
  if (operacao === "remove") return Math.max(0, atual - qtd);
  return atual + qtd;
};
